import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  Airplane,
  ArrowLeft,
  Bell,
  CarFront,
  ChevronRight,
  Envelope,
  GeoAlt,
  LightningCharge,
  Palette,
  ShieldCheck,
  Tools,
} from "react-bootstrap-icons";
import { toast } from "react-toastify";
import {
  car_home,
  dashboard_red_car,
  dashboard_special_deal,
  jeep_yellow_car,
  kirkos_logo_white_yellow,
  lexus_black_car
} from "../../assets/images";
import axiosInstance from "../../services/axios.instance";
import { API_URL } from "../../services/apiConfig";
import "../Customer/homepage/styles.css";
import "./styles.css";
import SEO from "../../components/SEO";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ICON_MAP: Record<string, any> = {
  CarFront,
  Airplane,
  Tools,
  ShieldCheck,
  Palette,
};

const IMAGE_MAP: Record<string, string> = {
  car_home,
  lexus_black_car,
  dashboard_special_deal,
  jeep_yellow_car,
  dashboard_red_car,
};

const ServiceComingSoonPage = () => {
  const { slug } = useParams();
  const [configs, setConfigs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const response = await axiosInstance.get(API_URL.SERVICE_CONFIGS);
        if (response.data && Array.isArray(response.data)) {
          setConfigs(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch service configs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigs();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [slug]);

  const service = useMemo(() => {
    if (!slug || configs.length === 0) return undefined;
    return configs.find((c) => c.slug === slug);
  }, [slug, configs]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (isLoading) {
    return (
      <div className="service-coming-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const ServiceIcon = ICON_MAP[service.icon] || Tools;
  const serviceImage = IMAGE_MAP[service.image] || service.image;

  const handleNotify = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!emailRegex.test(email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    toast.success(`Thanks. We'll keep you updated about ${service.label}.`);
    setEmail("");
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service?.label,
    "description": service?.subtitle,
    "provider": {
      "@type": "Organization",
      "name": "Kirkos Car Rental"
    },
    "areaServed": {
      "@type": "Country",
      "name": "AE"
    }
  };

  return (
    <div className="service-coming-page">
      <SEO
        title={`${service?.label || "Service"} | Coming Soon to Kirkos`}
        description={service?.subtitle || `Our ${service?.label} service is coming soon. Stay tuned for premium car rental and mobility solutions from Kirkos.`}
        keywords={`kirkos ${service?.label}, car rental, coming soon, mobility services`}
        schemaData={serviceSchema}
      />
      <div className="service-coming-page__ambient service-coming-page__ambient--left" />
      <div className="service-coming-page__ambient service-coming-page__ambient--right" />


      <main className="service-coming-page__hero">
        <div className="service-coming-page__backdrop" />
        <div className="service-coming-page__gold-trail service-coming-page__gold-trail--top" />
        <div className="service-coming-page__gold-trail service-coming-page__gold-trail--bottom" />

        <div className="service-coming-page__content">
          <div className="service-coming-page__copy">
            <div className="service-coming-page__eyebrow">
              <ServiceIcon size={16} />
              <span>{service?.navTitle}</span>
            </div>

            <h1 className="service-coming-page__headline">{service?.headline}</h1>
            <p className="service-coming-page__kicker">{service?.kicker}</p>
            <p className="service-coming-page__subtitle">{service?.subtitle}</p>

            <div className="service-coming-page__chips">
              {service?.availability?.map((item: string) => (
                <span key={item} className="service-coming-page__chip">
                  <GeoAlt size={12} />
                  {item}
                </span>
              ))}
            </div>

            <div className="service-coming-page__actions">
              <Link
                to={service?.primaryActionTo}
                className="service-coming-page__action service-coming-page__action--primary"
              >
                {service?.primaryActionLabel}
                <ChevronRight size={16} />
              </Link>
              <Link
                to={service?.secondaryActionTo}
                className="service-coming-page__action service-coming-page__action--secondary"
              >
                {service?.secondaryActionLabel}
              </Link>
            </div>
          </div>

          <div className="service-coming-page__visual" aria-hidden="true">
            <div
              className="service-coming-page__visual-halo"
              style={
                {
                  "--service-accent": service?.accent,
                  "--service-accent-soft": `${service?.accent}55`,
                } as CSSProperties
              }
            />
            <img src={serviceImage} alt="" className="service-coming-page__visual-image" />
          </div>
        </div>

        <section
          className="service-coming-page__panels"
          aria-label={`${service?.label} launch details`}
        >
          {service?.panels?.map((panel: any) => (
            <article className="service-coming-page__panel" key={panel.id || panel.title}>
              <h2>{panel?.title}</h2>
              <ul>
                {panel?.items?.map((item: string) => (
                  <li key={item}>
                    <LightningCharge size={14} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        {/* {service.status === "coming-soon" ? (
          <section
            className="service-coming-page__notify"
            aria-label={`Get notified about ${service.label}`}
          >
            <div className="service-coming-page__notify-head">
              <Bell size={18} />
              <h2>Notify Me</h2>
            </div>

            <form className="service-coming-page__notify-form" onSubmit={handleNotify}>
              <label className="service-coming-page__notify-input-wrap">
                <Envelope size={16} />
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={service.notifyPlaceholder}
                  aria-label={`Email for ${service.label} updates`}
                />
              </label>
              <button type="submit" className="service-coming-page__notify-button">
                Notify Me
              </button>
            </form>

            <div className="service-coming-page__footer-links">
              <Link to="/" className="service-coming-page__back-link">
                <ArrowLeft size={15} />
                Back to Home
              </Link>
              <Link to="/contactus" className="service-coming-page__footer-link">
                Speak to Kirkos
              </Link>
            </div>
          </section>
        ) : (
          <section className="service-coming-page__live-cta" aria-label={`${service.label} is live`}>
            <div className="service-coming-page__live-copy">
              <div className="service-coming-page__live-label">Live Service</div>
              <h2>{service.label} is already available through Kirkos.</h2>
              <p>
                Search the live fleet, compare options, and move directly into booking with the
                same premium Kirkos flow.
              </p>
            </div>

            <div className="service-coming-page__live-actions">
              <Link to="/" className="service-coming-page__action service-coming-page__action--primary">
                {service.primaryActionLabel}
                <ChevronRight size={16} />
              </Link>
              <Link
                to="/contactus"
                className="service-coming-page__action service-coming-page__action--secondary"
              >
                {service.secondaryActionLabel}
              </Link>
            </div>
          </section>
        )} */}
      </main>
    </div>
  );
};

export default ServiceComingSoonPage;

